import { isEmpty, uniqBy } from 'lodash';
import UsersDB from 'src/fb/users-db';
import { auth as useAuthStore } from 'src/store/auth';
import { userRoles } from 'src/utils/AppUtils';
import { bindDataBaseStore, bindDataBaseStoreByArrayOperator, unbindDataBaseStore } from 'src/utils/FirestoreUtils';

export default {
  /**
   * Fetch all users
   */
  async bindUsers(clinic = null) {
    const auth = useAuthStore();

    const userRole = auth.userDetails?.role || '';
    const userUnits = auth.userDetails?.units || [];

    /*
     * The binding outlives the page that opened it, so what it was built for has to be remembered.
     * Clinic alone will not do: the query below is shaped by role and units as well, and
     * reloadUserDetails replaces both during a session, which would otherwise leave someone bound to
     * units they no longer have. Held as a key rather than read off `users`, because a query that
     * matched nobody is still a binding and re-running it every time the page opens is what the
     * guard exists to prevent.
     */
    const bindingKey = JSON.stringify([clinic, userRole, [...userUnits].sort()]);

    if (clinic && this.boundKey === bindingKey) {
      /*
       * Still in flight when two components mount at once. Returning it rather than returning bare
       * makes the second caller wait for the users it is about to read, and see the error if the
       * binding fails. Null once it has settled, which is the case the guard is really for.
       */
      return this.pendingBinding;
    }

    /* Claimed before the binding resolves, so two components mounting at once do not both bind. */
    this.boundKey = bindingKey;

    const usersDb = new UsersDB();
    const binding = { store: this, property: 'users' };

    let pending;

    if ([userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(userRole)) {
      pending = bindDataBaseStore(binding, usersDb.getQuery([['clinic', '==', clinic]]), []);
    } else if (isEmpty(userUnits)) {
      pending = bindDataBaseStore(binding, usersDb.getQuery([
        ['clinic', '==', clinic],
        ['units', '==', []],
      ]), []);
    } else if (userUnits.length > 30) {
      pending = bindDataBaseStoreByArrayOperator(
        { ...binding, action: 'setUsers' },
        usersDb,
        {
          field: 'units',
          operator: 'array-contains-any',
          clauses: userUnits,
        },
        [
          ['clinic', '==', clinic],
        ],
        30,
      );
    } else {
      pending = bindDataBaseStore(binding, usersDb.getQuery([
        ['clinic', '==', clinic],
        ['units', 'array-contains-any', userUnits],
      ]), []);
    }

    this.pendingBinding = pending;

    try {
      return await pending;
    } catch (error) {
      /*
       * Keeping the key would make one failure permanent: every later call matches it and returns.
       * Gated on the binding still being the current one rather than on the key still matching,
       * because an unbind and a rebind of the same clinic reclaim that key: comparing keys, a
       * rejection arriving after that would clear the live binding's, and the next caller would read
       * the whole clinic again. The promise is what distinguishes them.
       */
      if (this.pendingBinding === pending) {
        this.boundKey = null;
      }

      throw error;
    } finally {
      if (this.pendingBinding === pending) {
        this.pendingBinding = null;
      }
    }
  },

  /**
   * Unbind users
   * */
  unbindUsers() {
    this.boundKey = null;
    this.pendingBinding = null;

    return unbindDataBaseStore({ store: this, property: 'users' }, []);
  },

  logout() {
    this.unbindUsers();
  },

  setUsers(users) {
    this.users = uniqBy(
      users.filter((user) => !user.isSupporting),
      'id',
    );
  },

};
